import React, { useContext, useEffect, useState } from "react";
import { Loader } from "../components/Loader";
import { useGatewayDigitalUserAccounts } from "./gateway/queries";
import { ProfileFieldMapHook } from "./profileFieldMap";
import {
  determineProfileFieldMapMetadata,
  determineProfileFieldMapping,
  PROFILE_FIELD_MAP_METADATA_DEFAULT,
  ProfileFieldMapMetadata,
} from "./profileFieldMapContextHelpers";

export interface IProfileFieldMapContext {
  useProfileFieldMap: ProfileFieldMapHook;
  metadata: ProfileFieldMapMetadata;

  _uninitialised: boolean;
}

// This should never happen, Provider should handle init and not render until it is
const ProfileFieldMapUsedBeforeInitialised: ProfileFieldMapHook = () => {
  throw Error("ProfileFieldMap used before initialised");
};

export const ProfileFieldMapContext =
  React.createContext<IProfileFieldMapContext>({
    useProfileFieldMap: ProfileFieldMapUsedBeforeInitialised,
    metadata: PROFILE_FIELD_MAP_METADATA_DEFAULT,
    _uninitialised: true,
  });

export const useProfileFieldMapChooser = (): IProfileFieldMapContext =>
  useContext<IProfileFieldMapContext>(ProfileFieldMapContext);

export const ProfileFieldMapProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [profileFieldMap, setProfileFieldMap] =
    useState<IProfileFieldMapContext>({
      useProfileFieldMap: ProfileFieldMapUsedBeforeInitialised,
      metadata: PROFILE_FIELD_MAP_METADATA_DEFAULT,
      _uninitialised: true,
    });

  const { data, loading, error } = useGatewayDigitalUserAccounts();
  useEffect(() => {
    if (data) {
      setProfileFieldMap({
        useProfileFieldMap: determineProfileFieldMapping(data),
        metadata: determineProfileFieldMapMetadata(data),
        _uninitialised: false,
      });
    }
  }, [data]);

  if (error) {
    throw error;
  }

  return (
    <ProfileFieldMapContext.Provider value={profileFieldMap}>
      <Loader
        isLoading={loading || profileFieldMap._uninitialised}
        variant="page"
      >
        {children}
      </Loader>
    </ProfileFieldMapContext.Provider>
  );
};
